import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Row, Col, Typography, Button, Spin, Input, Select, Pagination, Checkbox, Modal } from 'antd'
import {
  SearchOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  ProductActions,
  getPermissionGroups,
  getIsLoadingPermissionGroups,
  getPermissions,
  getIsLoadingPermissions,
  getPermissionsPageMeta,
  getPermissionsVersion,
  getModalVisibility,
  ModalName,
} from '~/Store/Products'
import { NoData } from '~/Components/NoData'
import { IPermission, IProduct } from '~/Services/Product.interface'
import { PermissionFormModal } from './PermissionFormModal'
import styles from './Products.module.less'

const { confirm } = Modal

const { Option } = Select
const DEFAULT_PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 500
const MIN_LIST_HEIGHT_PX = 200
const RESERVED_FOR_PAGINATION_PX = 72

// Reached via history.push(..., { product }) from the Products list row, same convention as
// EditApiKey.tsx — header/counts render instantly from data the list already loaded.
export const ProductDetail = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<{ product?: IProduct } | undefined>()
  const product = location.state?.product
  const dispatch = useAppDispatch()

  const permissionGroups = useAppSelector(getPermissionGroups)
  const isLoadingGroups = useAppSelector(getIsLoadingPermissionGroups)
  const permissions = useAppSelector(getPermissions)
  const isLoadingPermissions = useAppSelector(getIsLoadingPermissions)
  const pageMeta = useAppSelector(getPermissionsPageMeta)
  const permissionsVersion = useAppSelector(getPermissionsVersion)
  const formModalVisible = useAppSelector(getModalVisibility(ModalName.Permission))

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [group, setGroup] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const searchTimer = useRef<ReturnType<typeof setTimeout>>()

  // Content above the list wraps to a variable number of lines (group chips), so we measure where
  // the list actually starts instead of guessing a fixed offset to subtract from the viewport.
  const listRef = useRef<HTMLDivElement>(null)
  const [listHeight, setListHeight] = useState<number>()

  useLayoutEffect(() => {
    const updateListHeight = () => {
      if (!listRef.current) return
      const top = listRef.current.getBoundingClientRect().top
      setListHeight(
        Math.max(MIN_LIST_HEIGHT_PX, window.innerHeight - top - RESERVED_FOR_PAGINATION_PX),
      )
    }
    updateListHeight()
    window.addEventListener('resize', updateListHeight)
    return () => window.removeEventListener('resize', updateListHeight)
  }, [permissionGroups.length])

  useEffect(() => {
    if (!product) {
      history.replace('/Admin/Products')
      return
    }
    dispatch(ProductActions.getPermissionGroups(product.guid))
    // Prevent the outer document from scrolling (a stray px of overflow in the shared page chrome
    // otherwise jerks the whole page) — only the inner list, which scrolls on its own, should move.
    const previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousBodyOverflow
      clearTimeout(searchTimer.current)
      dispatch(ProductActions.resetProductDetail())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!product) return
    dispatch(
      ProductActions.searchPermissions({
        productId: product.guid,
        search: search || undefined,
        group,
        page,
        pageSize,
      }),
    )
    // permissionsVersion is bumped after a successful create/update/delete/bulk-delete — re-running
    // this same effect refetches the current page with whatever search/group/page is already active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, search, group, page, pageSize, permissionsVersion])

  // Group counts (chips) go stale once a permission is added/edited/deleted — refresh them too, but
  // skip the very first run since the mount effect above already fetched groups once.
  const isFirstVersionRef = useRef(true)
  useEffect(() => {
    if (isFirstVersionRef.current) {
      isFirstVersionRef.current = false
      return
    }
    if (product) {
      dispatch(ProductActions.getPermissionGroups(product.guid))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsVersion])

  // Case: deleting the last rows on a page removes that page entirely — fall back to the new last page
  // instead of showing an empty page 5 of 4.
  useEffect(() => {
    if (pageMeta && pageMeta.pageCount > 0 && page > pageMeta.pageCount) {
      setPage(pageMeta.pageCount)
    }
  }, [pageMeta])

  // Prunes stale selections in one place for every case that can change the visible rows (search,
  // group filter, page change, or a mutation-triggered refetch) instead of clearing ad hoc per handler.
  useEffect(() => {
    setSelectedIds(prev => prev.filter(id => permissions.some(p => p.guid === id)))
  }, [permissions])

  if (!product) return null

  // Search/group filter changes reset back to page 1 (Case 11/12).
  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setSearch(value.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleGroupToggle = (groupName: string) => {
    setGroup(prev => (prev === groupName ? undefined : groupName))
    setPage(1)
  }

  const handleGroupSelect = (value?: string) => {
    setGroup(value)
    setPage(1)
  }

  const handlePageChange = (nextPage: number, nextPageSize?: number) => {
    setPage(nextPage)
    if (nextPageSize && nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
    }
  }

  const handleAddClick = () => {
    dispatch(ProductActions.setPermissionSelected(undefined))
    dispatch(
      ProductActions.setModalVisibility({ name: ModalName.Permission, visible: true }),
    )
  }

  const handleEditClick = (permission: IPermission) => {
    dispatch(ProductActions.setPermissionSelected(permission))
    dispatch(
      ProductActions.setModalVisibility({ name: ModalName.Permission, visible: true }),
    )
  }

  const handleDeleteClick = (permission: IPermission) => {
    confirm({
      title: t('admin.confirmDeletePermission', { name: permission.claimValue }),
      icon: <ExclamationCircleFilled />,
      okText: t('admin.delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk() {
        dispatch(
          ProductActions.deletePermission({
            productId: product.guid,
            permissionId: permission.guid,
          }),
        )
      },
    })
  }

  const handleToggleSelect = (permissionId: string, checked: boolean) => {
    setSelectedIds(prev =>
      checked ? [...prev, permissionId] : prev.filter(id => id !== permissionId),
    )
  }

  const handleToggleSelectAll = (checked: boolean) => {
    const pageIds = permissions.map(p => p.guid)
    setSelectedIds(prev =>
      checked
        ? Array.from(new Set([...prev, ...pageIds]))
        : prev.filter(id => pageIds.indexOf(id) === -1),
    )
  }

  const handleBulkDeleteClick = () => {
    confirm({
      title: t('admin.confirmBulkDeletePermissions', { count: selectedIds.length }),
      icon: <ExclamationCircleFilled />,
      okText: t('admin.delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk() {
        dispatch(
          ProductActions.bulkDeletePermissions({
            productId: product.guid,
            permissionIds: selectedIds,
          }),
        )
      },
    })
  }

  const isFiltered = !!search || !!group
  const allOnPageSelected =
    permissions.length > 0 && permissions.every(p => selectedIds.indexOf(p.guid) !== -1)
  const someOnPageSelected = permissions.some(p => selectedIds.indexOf(p.guid) !== -1)

  return (
    <div className={styles.Container}>
      {formModalVisible && <PermissionFormModal product={product} />}

      <Row align="middle" style={{ marginBottom: '16px' }}>
        <Col flex="auto">
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            {product.name}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t('admin.clientId')}: {product.clientId}
          </Typography.Text>
        </Col>
        <Col style={{ textAlign: 'right' }}>
          <div>
            {t('admin.permissionGroups')}: <b>{product.permissionGroupCount}</b>
          </div>
          <div>
            {t('admin.permissions')}: <b>{product.permissionCount}</b>
          </div>
        </Col>
      </Row>

      <Row align="middle" style={{ marginBottom: '16px' }}>
        <Col flex="auto">
          {someOnPageSelected && (
            <Row align="middle" gutter={12}>
              <Col>
                {t('admin.permissionsSelected', { count: selectedIds.length })}
              </Col>
              <Col>
                <Button danger icon={<DeleteOutlined />} onClick={handleBulkDeleteClick}>
                  {t('admin.deleteSelected')}
                </Button>
              </Col>
            </Row>
          )}
        </Col>
        <Col>
          <Button type="primary" icon={<PlusCircleOutlined />} onClick={handleAddClick}>
            {t('admin.addPermission')}
          </Button>
        </Col>
      </Row>

      <Row gutter={12} style={{ marginBottom: '16px' }}>
        <Col flex="auto">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('admin.searchPermissionsPlaceholder')}
            value={searchInput}
            onChange={evt => handleSearchChange(evt.target.value)}
          />
        </Col>
        <Col style={{ width: '260px' }}>
          <Select
            style={{ width: '100%' }}
            value={group}
            placeholder={t('admin.allGroups')}
            allowClear
            onChange={handleGroupSelect}
          >
            {permissionGroups.map(g => (
              <Option key={g.guid} value={g.name}>
                {`${g.name} (${g.permissionCount})`}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Spin spinning={isLoadingGroups}>
        {permissionGroups.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Typography.Text strong>{t('admin.permissionGroups')}</Typography.Text>
            <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
              {permissionGroups.map(g => {
                const active = group === g.name
                return (
                  <Col key={g.guid}>
                    <Button
                      size="small"
                      type={active ? 'primary' : 'default'}
                      icon={active ? <CaretDownOutlined /> : <CaretRightOutlined />}
                      onClick={() => handleGroupToggle(g.name)}
                    >
                      {`${g.name} · ${g.permissionCount}`}
                    </Button>
                  </Col>
                )
              })}
            </Row>
          </div>
        )}
      </Spin>

      <div
        ref={listRef}
        style={{
          height: listHeight,
          overflowY: 'auto',
          paddingRight: '16px',
          marginRight: '-16px',
        }}
      >
        <Spin spinning={isLoadingPermissions}>
          <div className={styles.HeaderRow}>
            <Row align="middle">
              <Col xs={2} md={2}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={someOnPageSelected && !allOnPageSelected}
                  onChange={evt => handleToggleSelectAll(evt.target.checked)}
                />
              </Col>
              <Col xs={7} md={7}>{t('admin.group')}</Col>
              <Col xs={11} md={11}>{t('admin.name')}</Col>
              <Col xs={4} md={4} style={{ textAlign: 'right' }}>
                {t('admin.actions')}
              </Col>
            </Row>
          </div>

          {permissions.length === 0 ? (
            <NoData
              content={
                isFiltered
                  ? t('admin.noPermissionsFound')
                  : t('admin.noPermissionsYet')
              }
            />
          ) : (
            permissions.map(permission => (
              <div className={styles.Item} key={permission.guid}>
                <Row align="middle">
                  <Col xs={2} md={2}>
                    <Checkbox
                      checked={selectedIds.indexOf(permission.guid) !== -1}
                      onChange={evt => handleToggleSelect(permission.guid, evt.target.checked)}
                    />
                  </Col>
                  <Col xs={7} md={7}>
                    {permission.permissionGroup?.name || '-'}
                  </Col>
                  <Col xs={11} md={11}>
                    {permission.claimValue || permission.displayName}
                  </Col>
                  <Col xs={4} md={4}>
                    <div className={styles.Actions} style={{ justifyContent: 'flex-end' }}>
                      <EditOutlined
                        style={{ color: '#1890ff' }}
                        onClick={() => handleEditClick(permission)}
                      />
                      <DeleteOutlined
                        style={{ color: 'red' }}
                        onClick={() => handleDeleteClick(permission)}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            ))
          )}
        </Spin>
      </div>

      {!!pageMeta?.queryCount && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={pageMeta.queryCount}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            showSizeChanger
            pageSizeOptions={['20', '50', '100']}
          />
        </div>
      )}
    </div>
  )
}
